import {
  FormEvent,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import Markdown from 'markdown-to-jsx';
import styled, { css, keyframes } from 'styled-components';

import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import { getUserId } from '@paradigm/store/src/auth/selectors';
import { usePreferencesStore } from '@paradigm/features/src/preferences/store';

import {
  clearAgentSession,
  clearAgentUrl,
  deriveDefaultAgentUrl,
  getAgentToken,
  getAgentUrl,
  getDeviceToken,
  isValidAgentUrl,
  setAgentToken,
} from '#/api/terminal/agent-url';
import { clearChatHistory } from '#/api/terminal/chat-history-storage';
import { ChatMessage } from '#/api/terminal/types';
import {
  useAgentActions,
  useAgentState,
} from '#/features/terminal/agent-context';
import { useTerminalDrawerState } from '#/features/terminal/drawer-context';

import { rewriteModelMentions } from '#/api/terminal/models-config';

import ApprovalCard from './ApprovalCard';
import ModelPicker from './ModelPicker';

type SlashCommandSuggestion = {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly insertText: string;
  readonly kind: 'command' | 'skill';
};

const BUILT_IN_SLASH_COMMANDS: readonly SlashCommandSuggestion[] = [
  {
    id: 'builtin-help',
    label: '/help',
    description: 'Show OpenClaw help',
    insertText: '/help',
    kind: 'command',
  },
  {
    id: 'builtin-commands',
    label: '/commands',
    description: 'Show available commands',
    insertText: '/commands',
    kind: 'command',
  },
  {
    id: 'builtin-skills',
    label: '/skills',
    description: 'List active skills',
    insertText: '/skills',
    kind: 'command',
  },
  {
    id: 'builtin-tools',
    label: '/tools',
    description: 'Show current tools',
    insertText: '/tools',
    kind: 'command',
  },
  {
    id: 'builtin-agents',
    label: '/agents',
    description: 'List thread-bound agents',
    insertText: '/agents',
    kind: 'command',
  },
  {
    id: 'builtin-model',
    label: '/model',
    description: 'Show or change the model',
    insertText: '/model',
    kind: 'command',
  },
  {
    id: 'builtin-clear',
    label: '/clear',
    description: 'Clear the current chat context',
    insertText: '/clear',
    kind: 'command',
  },
  {
    id: 'builtin-compact',
    label: '/compact',
    description: 'Compact session context',
    insertText: '/compact',
    kind: 'command',
  },
];

export function getSlashCommandQuery(input: string): string | null {
  if (!input.startsWith('/')) return null;
  if (input.includes('\n')) return null;
  const trimmedStart = input.trimStart();
  if (trimmedStart !== input) return null;
  const query = input.slice(1);
  if (query.includes(' ')) return null;
  return query.toLowerCase();
}

export function buildSlashCommandSuggestions(
  suggestions: readonly SlashCommandSuggestion[],
  query: string | null,
): readonly SlashCommandSuggestion[] {
  if (query == null) return [];
  return suggestions
    .filter((suggestion) =>
      suggestion.label.toLowerCase().slice(1).includes(query),
    )
    .slice(0, 10);
}

// Single-line height matches the composer's collapsed state; anything
// taller flips the send / slash buttons to bottom-align.
const SINGLE_LINE_TEXTAREA_HEIGHT = 32;

interface ChatViewProps {
  /**
   * Hide the top "+ New chat / Temp Chat" bar. Used by hosts that
   * present their own session controls outside the chat (e.g. a
   * Recents-driven layout where the buttons live in the sidebar).
   * Defaults to `false` so the existing drawer/side-panel embedding
   * keeps the bar.
   */
  readonly hideNewChatBar?: boolean;
}

export default function ChatView({ hideNewChatBar }: ChatViewProps = {}) {
  const agentState = useAgentState();
  const userId = useSelector(getUserId);
  // Set when the user clicks "Disconnect"; surfaces the setup form again
  // (pre-filled with the previously-used URL + token) so reconnecting is a
  // one-click action rather than a re-entry. Cleared when the form is
  // submitted, at which point ChatInterface remounts.
  const [manualDisconnect, setManualDisconnect] = useState(false);

  if (userId == null) {
    return <SignedOutState />;
  }

  const userIdStr = String(userId);

  const showSetupForm =
    agentState.agentUrl == null ||
    agentState.agentUrl.length === 0 ||
    manualDisconnect;

  if (showSetupForm) {
    return (
      <AgentUrlSetup
        userId={userIdStr}
        onConnected={() => setManualDisconnect(false)}
      />
    );
  }

  return (
    <ChatInterface
      userId={userIdStr}
      onManualDisconnect={() => setManualDisconnect(true)}
      hideNewChatBar={hideNewChatBar}
    />
  );
}

function SignedOutState() {
  return (
    <Container>
      <EmptyState>
        <Title>Sign in to use the DIME terminal</Title>
        <Description>
          The DIME terminal pairs an AI agent with your paradigm account so you
          can ask about block trades flowing through the tape.
        </Description>
      </EmptyState>
    </Container>
  );
}

function AgentUrlSetup({
  userId,
  onConnected,
}: {
  readonly userId: string;
  readonly onConnected?: () => void;
}) {
  const user = useSelector((state) => state.auth.user);
  const deskTicker = user?.trading_desk?.ticker;
  const numericUserId = parseInt(userId, 10);
  const defaultUrl =
    deskTicker != null && !Number.isNaN(numericUserId)
      ? deriveDefaultAgentUrl(deskTicker, numericUserId)
      : '';
  // Pre-fill from localStorage first (this device's last pairing) and fall
  // back to the cross-device preferences blob so a manual disconnect — which
  // wipes the local cache — still re-mounts the form with the user's URL and
  // pairing token for one-click reconnect. First-time users fall back to the
  // desk-derived default URL so they have a sensible starting point.
  const persistedUrl = usePreferencesStore((s) => s.aiAgentUrl);
  const persistedToken = usePreferencesStore((s) => s.aiAgentToken);
  const [url, setUrl] = useState(
    () => getAgentUrl(userId) ?? persistedUrl ?? defaultUrl,
  );
  const [token, setToken] = useState(
    () => getAgentToken(userId) ?? persistedToken ?? '',
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const agentActions = useAgentActions();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedUrl = url.trim();
    if (trimmedUrl.length === 0) return;
    if (!isValidAgentUrl(trimmedUrl)) return;
    // Only clear the existing device-token session when the URL is changing —
    // a stale token from a prior OpenClaw instance must not get replayed
    // against a different one. On same-URL reconnects (the common disconnect
    // → connect flow) keep the token so pairing isn't required again.
    const previousUrl = getAgentUrl(userId);
    if (previousUrl !== trimmedUrl) {
      clearAgentSession(userId);
    }
    if (token.trim().length > 0) {
      setAgentToken(userId, token.trim());
    }
    agentActions.setUrl(trimmedUrl);
    agentActions.connect(trimmedUrl);
    onConnected?.();
  };

  const isUrlValid = url.trim().length === 0 || isValidAgentUrl(url.trim());

  return (
    <Container>
      <EmptyState>
        <Title>Connect your AI agent</Title>
        <Description>
          Enter the OpenClaw gateway URL. After it connects, pair this device
          from the terminal tab to activate block-trade analysis.
        </Description>
        <SetupForm onSubmit={handleSubmit}>
          <UrlInput
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={
              defaultUrl.length > 0 ? defaultUrl : 'wss://your-agent-host'
            }
            autoComplete="off"
            $invalid={!isUrlValid}
          />
          <AdvancedToggle
            type="button"
            onClick={() => setShowAdvanced((visible) => !visible)}
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced'}
          </AdvancedToggle>
          {showAdvanced ? (
            <UrlInput
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Gateway token"
              autoComplete="off"
            />
          ) : null}
          <ConnectButton
            type="submit"
            disabled={url.trim().length === 0 || !isUrlValid}
          >
            Connect gateway
          </ConnectButton>
        </SetupForm>
      </EmptyState>
    </Container>
  );
}

function ChatInterface({
  userId,
  onManualDisconnect,
  hideNewChatBar,
}: {
  readonly userId: string;
  readonly onManualDisconnect?: () => void;
  readonly hideNewChatBar?: boolean;
}) {
  const state = useAgentState();
  const actions = useAgentActions();
  const [input, setInput] = useState('');
  const [pairingState, setPairingState] = useState<
    'idle' | 'pending' | 'paired' | 'error'
  >('idle');
  const [pairingError, setPairingError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const drawerState = useTerminalDrawerState();
  // Multi-line tracks whether the textarea has wrapped past one line so
  // the slash / send buttons can pin to bottom rather than vertical-
  // center (matches the Paradex composer behaviour).
  const [isMultilineInput, setIsMultilineInput] = useState(false);
  // Keyboard cursor into the slash-command suggestion list.
  const [slashIndex, setSlashIndex] = useState(0);
  // Set when the user clicks the `/` trigger button (vs. typing `/`
  // themselves). Forces the menu open even when the input is empty.
  const [manualSlashOpen, setManualSlashOpen] = useState(false);
  /**
   * Whether the user is currently pinned at the bottom of the messages
   * list. Updated on every scroll. We only auto-scroll when this is
   * true so that scrolling up to read earlier content doesn't get
   * yanked back to the bottom on the next streaming delta.
   */
  const stickToBottomRef = useRef(true);

  // Auto-connect on mount if a URL is configured but we're not yet
  // connected. Mount-only by design — depending on `state` would cause
  // reconnect loops on every status change.
  useEffect(() => {
    if (state.agentUrl != null && state.status === 'disconnected') {
      actions.connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the user lands on the Chat tab — initial mount, tab switch
  // back, or just-switched session — focus the composer so they can
  // start typing without an extra click. Tabs stay mounted, so this
  // effect fires explicitly when activeTab transitions to 'chat'.
  useEffect(() => {
    if (drawerState.activeTab !== 'chat') return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [drawerState.activeTab, state.activeSessionId]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (el == null) return;
    // 24px slack so a tiny gap from a smooth-scroll animation still
    // counts as "at the bottom".
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }, []);

  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    if (el == null) return;
    if (!stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [state.messages, state.processing]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (trimmed.length === 0) return;
    if (needsPairing(userId, pairingState, state.status)) return;
    actions.sendMessage(trimmed);
    setInput('');
    setManualSlashOpen(false);
    inputRef.current?.focus();
  }, [input, actions, userId, pairingState, state.status]);

  const isConnected = state.status === 'authenticated';

  // Slash-command suggestions: open when the user types `/` *or* clicks
  // the trigger button. `manualSlashOpen` keeps the menu visible while
  // the input is still empty so the user can pick from the full list
  // before typing anything.
  const slashCommandQuery =
    getSlashCommandQuery(input) ?? (manualSlashOpen ? '' : null);
  const slashSuggestions = useMemo(
    () =>
      buildSlashCommandSuggestions(BUILT_IN_SLASH_COMMANDS, slashCommandQuery),
    [slashCommandQuery],
  );
  const showSlashCommands =
    isConnected && slashCommandQuery != null && slashSuggestions.length > 0;

  // Reset the keyboard cursor whenever the filter changes so the
  // highlight always starts on the first row of the new result set.
  useEffect(() => {
    setSlashIndex(0);
  }, [slashCommandQuery]);

  // Clamp the cursor when suggestions shrink below the current index
  // (e.g. typing narrows the result list).
  useEffect(() => {
    if (slashIndex >= slashSuggestions.length) {
      setSlashIndex(Math.max(0, slashSuggestions.length - 1));
    }
  }, [slashIndex, slashSuggestions.length]);

  const selectSlashCommand = useCallback(
    (suggestion: SlashCommandSuggestion) => {
      setInput(suggestion.insertText);
      setManualSlashOpen(false);
      inputRef.current?.focus();
    },
    [],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSlashCommands) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((index) => (index + 1) % slashSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex(
          (index) =>
            (index - 1 + slashSuggestions.length) % slashSuggestions.length,
        );
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const suggestion = slashSuggestions[slashIndex];
        if (suggestion != null) {
          selectSlashCommand(suggestion);
        }
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const suggestion = slashSuggestions[slashIndex];
        if (suggestion != null) {
          selectSlashCommand(suggestion);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
        setManualSlashOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-grow the textarea up to a cap as the user adds rows. Track
  // whether the input has wrapped so the send button can pin to the
  // bottom rather than vertical-center once the textarea grows.
  useLayoutEffect(() => {
    const ta = inputRef.current;
    if (ta == null) return;
    ta.style.height = 'auto';
    const next = Math.min(ta.scrollHeight, 200);
    ta.style.height = `${next}px`;
    setIsMultilineInput(ta.scrollHeight > SINGLE_LINE_TEXTAREA_HEIGHT);
  }, [input]);

  const handleDisconnect = () => {
    // Drop this device's cached pairing — URL, pairing token, device token —
    // so a stale device token from a prior OpenClaw instance can't get
    // replayed on reconnect. The cross-device preferences blob is kept, so
    // the setup form (and the agent-context hydration on next mount) can
    // re-populate URL + pairing token from there for one-click reconnect.
    //
    // Chat history is cleared too: the local cache is keyed by user only,
    // so on a reconnect to a *different* OpenClaw instance it would briefly
    // repaint the prior instance's transcript before `chat.history` RPC
    // catches up — and the merge logic only appends, it never evicts
    // orphans. Same-instance reconnects rehydrate from the gateway's
    // `chat.history` tail (up to 1000 messages).
    clearAgentSession(userId);
    clearAgentUrl(userId);
    clearChatHistory(userId);
    actions.disconnect();
    onManualDisconnect?.();
  };

  const handleReconnect = () => {
    actions.connect();
  };

  const handlePairDevice = async () => {
    setPairingState('pending');
    setPairingError(null);
    try {
      await actions.requestPairing();
      setPairingState('paired');
      actions.connect();
    } catch (err) {
      setPairingState('error');
      setPairingError(err instanceof Error ? err.message : 'Pairing failed');
    }
  };

  const shouldPair = needsPairing(userId, pairingState, state.status);
  // Only the explicit "Thinking" indicator counts as in-flight from the
  // user's POV. `state.processing` is set optimistically in
  // `sendMessage` and cleared on first delta / done / error — i.e. it's
  // alive only between "user pressed Send" and "first token of the
  // response". Background frames (heartbeat echoes, unprompted system
  // messages) never set processing, so they no longer flip the button
  // to Stop.
  const isProcessing = state.processing != null;
  const activeSession = state.sessions.find(
    (s) => s.sessionId === state.activeSessionId,
  );

  const handleNewChatClick = useCallback(() => {
    // If the active session is already an empty persistent chat, just
    // refocus the composer instead of spawning a second blank session —
    // saves the user from accumulating zombie "New Chat" rows when they
    // tap "+ New chat" repeatedly.
    if (
      activeSession != null &&
      activeSession.isTemporary !== true &&
      state.messages.length === 0
    ) {
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    actions.createSession({ folder: 'paradigm' });
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [actions, activeSession, state.messages.length]);

  return (
    <Container>
      {hideNewChatBar === true ? null : (
        <NewChatBar>
          <NewChatButton type="button" onClick={handleNewChatClick}>
            + New chat
          </NewChatButton>
          {activeSession?.isTemporary === true ? (
            <TemporaryBadge>Temp Chat - Not Saved</TemporaryBadge>
          ) : null}
          <TemporaryChatButton
            type="button"
            onClick={() => {
              actions.createSession({
                folder: 'paradigm',
                isTemporary: true,
              });
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            title="Start a one-off chat that won't be saved in Recents"
          >
            <TemporaryIcon />
            Temp Chat
          </TemporaryChatButton>
        </NewChatBar>
      )}
      <StatusBar>
        <StatusDot $status={state.status} />
        <StatusText>{statusLabel(state)}</StatusText>
        <AgentUrlText title={state.agentUrl ?? ''}>
          {state.agentUrl}
        </AgentUrlText>
        <StatusActions>
          {state.status === 'disconnected' || state.status === 'error' ? (
            <IconButton
              type="button"
              onClick={handleReconnect}
              aria-label="Reconnect"
              title="Reconnect"
            >
              <ReconnectIcon />
            </IconButton>
          ) : null}
          <IconButton
            type="button"
            onClick={handleDisconnect}
            aria-label="Disconnect"
            title="Disconnect"
          >
            <CloseIcon />
          </IconButton>
        </StatusActions>
      </StatusBar>

      {shouldPair ? (
        <PairingBanner>
          <PairingCopy>
            <strong>Pair this device</strong>
            <span>
              Approve the request on the OpenClaw host, then the terminal will
              reconnect as an operator.
            </span>
            {pairingError != null ? (
              <PairingError>{pairingError}</PairingError>
            ) : null}
          </PairingCopy>
          <ConnectButton
            type="button"
            onClick={handlePairDevice}
            disabled={pairingState === 'pending'}
          >
            {pairingState === 'pending' ? 'Pairing…' : 'Pair this device'}
          </ConnectButton>
        </PairingBanner>
      ) : null}

      <Messages
        ref={messagesContainerRef}
        onScroll={handleScroll}
        data-testid="terminal-messages"
      >
        {state.messages.length === 0 && state.processing == null ? (
          <Hint>
            Ask a question — e.g.{' '}
            <em>“What&rsquo;s flowing through the tape?”</em> or{' '}
            <em>“Summarize the last hour of BTC block trades”</em>.
          </Hint>
        ) : null}
        {state.messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            onRespondApproval={actions.respondToApproval}
          />
        ))}
        {state.processing != null ? (
          <ProcessingRow>
            <Dots />
            <span>
              {state.processing.label}
              {state.processing.detail != null
                ? ` — ${state.processing.detail}`
                : ''}
            </span>
          </ProcessingRow>
        ) : null}
      </Messages>

      <InputRow>
        <ComposerColumn>
          <Composer>
            {showSlashCommands && (
              <SlashCommandMenu role="listbox" aria-label="Slash commands">
                {slashSuggestions.map((suggestion, index) => (
                  <SlashCommandOption
                    key={suggestion.id}
                    type="button"
                    role="option"
                    aria-selected={index === slashIndex}
                    $active={index === slashIndex}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectSlashCommand(suggestion);
                    }}
                    onMouseEnter={() => setSlashIndex(index)}
                  >
                    <SlashCommandName>{suggestion.label}</SlashCommandName>
                    <SlashCommandDescription>
                      {suggestion.description}
                    </SlashCommandDescription>
                  </SlashCommandOption>
                ))}
              </SlashCommandMenu>
            )}
            <SlashTriggerButton
              type="button"
              onClick={() => {
                setManualSlashOpen((prev) => !prev);
                inputRef.current?.focus();
              }}
              disabled={!isConnected || shouldPair}
              aria-label="Show slash commands"
              title="Show slash commands"
              aria-pressed={manualSlashOpen}
              $alignBottom={isMultilineInput}
            >
              <SlashIcon />
            </SlashTriggerButton>
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (manualSlashOpen && !e.target.value.startsWith('/')) {
                  setManualSlashOpen(false);
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                shouldPair
                  ? 'Pair this device to start chatting...'
                  : 'Ask about block trades...'
              }
              rows={1}
              disabled={shouldPair}
              aria-autocomplete="list"
              aria-expanded={showSlashCommands}
            />
            <SendButton
              type="button"
              onClick={isProcessing ? actions.stopProcessing : handleSend}
              disabled={
                isProcessing ? false : shouldPair || input.trim().length === 0
              }
              aria-label={isProcessing ? 'Stop' : 'Send message'}
              $alignBottom={isMultilineInput}
            >
              {isProcessing ? <StopIcon /> : <SendIcon />}
            </SendButton>
          </Composer>
          {state.models.length > 0 && (
            <ComposerFooter>
              <ModelPicker
                models={state.models}
                selectedId={state.selectedModelId}
                onSelect={actions.setSelectedModel}
              />
            </ComposerFooter>
          )}
        </ComposerColumn>
      </InputRow>
    </Container>
  );
}

function needsPairing(
  userId: string,
  pairingState: 'idle' | 'pending' | 'paired' | 'error',
  status: ReturnType<typeof useAgentState>['status'],
): boolean {
  if (pairingState === 'paired') return false;
  if (status !== 'authenticated') return false;
  return getAgentToken(userId) == null && getDeviceToken(userId) == null;
}

function statusLabel(state: ReturnType<typeof useAgentState>): string {
  switch (state.status) {
    case 'authenticated':
    case 'connected':
      return 'Connected';
    case 'connecting':
      return 'Connecting…';
    case 'authenticating':
      return 'Authenticating…';
    case 'error':
      return state.errorMessage ?? 'Connection error';
    default:
      return 'Disconnected';
  }
}

const MessageRow = memo(function MessageRow({
  message,
  onRespondApproval,
}: {
  readonly message: ChatMessage;
  readonly onRespondApproval: (approvalId: string, approved: boolean) => void;
}) {
  if (message.approval != null) {
    return (
      <Bubble $role="assistant">
        <ApprovalCard
          approval={message.approval}
          onRespond={onRespondApproval}
        />
      </Bubble>
    );
  }
  const rendered =
    message.role === 'assistant'
      ? rewriteModelMentions(message.content)
      : message.content;
  return (
    <Bubble $role={message.role}>
      <Markdown options={MARKDOWN_OPTIONS}>{rendered}</Markdown>
      {message.isStreaming === true ? <StreamingDot /> : null}
    </Bubble>
  );
});

const MARKDOWN_OPTIONS = {
  forceBlock: false,
  overrides: {
    a: {
      props: { target: '_blank', rel: 'noreferrer noopener' },
    },
  },
};

// --- styling ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* flex: 1 (not height: 100%) — height: 100% inside a flex parent
     evaluates against the parent's resolved height and can be flaky
     while the layout is still being computed. flex: 1 + min-height: 0
     reliably claims the parent's remaining space and lets the Messages
     list scroll inside. */
  flex: 1;
  min-height: 0;
  width: 100%;
  background: var(--bg-color-container, #0e131a);
  color: var(--text-primary, #fff);
  font-size: 12px;
`;

const EmptyState = styled.div`
  margin: auto;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 480px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const Description = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 20px;
  line-height: 1.5;
`;

const SetupForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const UrlInput = styled.input<{ $invalid?: boolean }>`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ $invalid }) =>
      $invalid === true
        ? 'var(--error-color, #f44336)'
        : 'var(--border-color)'};
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: var(--font-mono, monospace);

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-color: ${paradigmLightBlue};
  }
`;

const ConnectButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background: ${paradigmLightBlue};
  color: var(--cta-text-color, #fff);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AdvancedToggle = styled.button`
  align-self: center;
  border: none;
  background: none;
  color: var(--text-secondary);
  font-size: 11px;
  cursor: pointer;
  padding: 2px 0;

  &:hover {
    color: var(--text-primary);
  }
`;

const PairingBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--divider-color, #1f2937);
  background: rgba(255, 255, 255, 0.04);
`;

const PairingCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: var(--text-secondary);
  line-height: 1.4;

  strong {
    color: var(--text-primary);
    font-size: 12px;
  }
`;

const PairingError = styled.span`
  color: var(--error-color, #f44336);
`;

const NewChatBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 0 12px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--divider-color, #1f2937);
`;

const NewChatButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 400;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg, rgba(255, 255, 255, 0.08));
  }
`;

const TemporaryBadge = styled.span`
  margin-left: auto;
  padding: 3px 10px;
  border-radius: 999px;
  border: none;
  background: var(--hover-bg, rgba(255, 255, 255, 0.08));
  font-size: 10px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1;
  white-space: nowrap;
`;

// Right-aligned Temp Chat button. The `margin-left: auto` here pushes
// it to the right edge when no badge is rendered; when the badge is
// present (active temp chat), the badge takes the auto-margin and the
// button sits flush to its right.
const TemporaryChatButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg, rgba(255, 255, 255, 0.08));
  }
`;

function TemporaryIcon() {
  return (
    <TemporaryIconSvg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 6.5V10.75L12.75 13.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="10"
        cy="10"
        r="9.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="3 6"
      />
    </TemporaryIconSvg>
  );
}

const TemporaryIconSvg = styled.svg`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

function StopIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3" y="3" width="10" height="10" rx="1.5" fill="currentColor" />
    </svg>
  );
}

function SlashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 17 17"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="0.5"
        y="0.5"
        width="16"
        height="16"
        rx="4"
        stroke="currentColor"
      />
      <path
        d="M10.7148 3.86816L7.39258 13.1318H6.28516L9.61328 3.86816H10.7148Z"
        fill="currentColor"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 13V3M8 3L3.5 7.5M8 3L12.5 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Two curved arrows in a loop — used for the Reconnect action. */
function ReconnectIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.5 7.5a5.5 5.5 0 0 1 9.4-3.9M13.5 8.5a5.5 5.5 0 0 1-9.4 3.9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.9 2v2.6h-2.6M4.1 14v-2.6h2.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Standard close X — used for the Disconnect action. */
function CloseIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.5 3.5l9 9M12.5 3.5l-9 9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--divider-color, #1f2937);
  font-size: 11px;
  color: var(--text-secondary);
`;

const StatusDot = styled.span<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) =>
    $status === 'authenticated' || $status === 'connected'
      ? 'var(--accent-color, #4caf50)'
      : $status === 'connecting' || $status === 'authenticating'
      ? 'var(--warning-color, #ff9800)'
      : $status === 'error' || $status === 'disconnected'
      ? 'var(--error-color, #f44336)'
      : 'var(--text-secondary)'};
`;

const StatusText = styled.span`
  font-weight: 500;
  color: var(--text-primary);
`;

const AgentUrlText = styled.span`
  flex: 1;
  font-family: var(--font-mono, monospace);
  font-size: 10px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #fff;
    background: var(--hover-bg, rgba(255, 255, 255, 0.08));
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Messages = styled.div`
  flex: 1;
  /* min-height: 0 lets overflow-y engage in a flex column. Without it
     the messages list grows to fit its content and shoves the input
     row off-screen on long agent replies. */
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hint = styled.div`
  margin: auto;
  color: var(--text-secondary);
  font-size: 12px;
  text-align: center;
  max-width: 360px;
  line-height: 1.5;
`;

const Bubble = styled.div<{ $role: ChatMessage['role'] }>`
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 8px;
  word-break: break-word;
  ${({ $role }) =>
    $role === 'user'
      ? css`
          align-self: flex-end;
          background: ${paradigmLightBlue};
          color: var(--cta-text-color, #fff);
        `
      : css`
          align-self: flex-start;
          background: rgba(255, 255, 255, 0.05);
          color: var(--text-primary);
        `}

  p {
    margin: 0;
  }
  p + p {
    margin-top: 6px;
  }
  pre {
    background: rgba(0, 0, 0, 0.3);
    padding: 6px 8px;
    border-radius: 4px;
    /* Cap code-block height with its own internal scroll. Without this
       a large JSON paste (e.g. "Analyze in Terminal" trade snapshots)
       inflates the bubble to ~80% of the panel height and pushes the
       agent's reply out of the visible area. */
    max-height: 240px;
    overflow: auto;
    margin: 0;
  }
  code {
    font-family: var(--font-mono, monospace);
    font-size: 11px;
  }
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

const StreamingDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  margin-left: 4px;
  animation: ${blink} 1.2s infinite;
`;

const ProcessingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 11px;
  padding: 4px 8px;
`;

function Dots() {
  return (
    <DotsContainer>
      <Dot />
      <Dot />
      <Dot />
    </DotsContainer>
  );
}

const DotsContainer = styled.span`
  display: inline-flex;
  gap: 3px;
`;

const Dot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  animation: ${blink} 1.2s infinite;
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid var(--divider-color, #1f2937);
`;

const ComposerColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ComposerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 6px;
`;

const Composer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 6px 10px;
  background: var(--bg-color-container, rgba(255, 255, 255, 0.04));
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 8px;

  &:focus-within {
    border-color: var(--text-secondary, rgba(255, 255, 255, 0.4));
  }
`;

const SlashTriggerButton = styled.button<{ $alignBottom?: boolean }>`
  flex-shrink: 0;
  align-self: ${({ $alignBottom }) =>
    $alignBottom === true ? 'flex-end' : 'center'};
  width: 22px;
  height: 22px;
  padding: 0;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  color: var(--text-secondary, rgba(255, 255, 255, 0.6));
  cursor: pointer;
  opacity: 0.7;

  &:hover:not(:disabled),
  &[aria-pressed='true'] {
    opacity: 1;
    color: var(--text-primary, #fff);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SlashCommandMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 6px);
  z-index: 2;
  max-height: 260px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 8px;
  background: var(--bg-color-container, #0e131a);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
`;

const SlashCommandOption = styled.button<{ $active: boolean }>`
  display: grid;
  grid-template-columns: minmax(8ch, 18ch) minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 6px 8px;
  border: 0;
  border-radius: 4px;
  background: ${({ $active }) =>
    $active ? 'var(--hover-bg, rgba(255, 255, 255, 0.08))' : 'transparent'};
  color: var(--text-primary);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg, rgba(255, 255, 255, 0.08));
  }
`;

const SlashCommandName = styled.span`
  font-family: var(--font-mono, monospace);
  font-size: 0.75rem;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SlashCommandDescription = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 0.6875rem;
`;

const Input = styled.textarea`
  flex: 1;
  min-width: 0;
  min-height: 22px;
  max-height: 200px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-primary, #fff);
  caret-color: var(--text-primary, #fff);
  font-size: 0.8125rem;
  font-family: inherit;
  line-height: 22px;
  resize: none;
  /* JS clamps grow to <= 200px, so the textarea never actually scrolls.
     Hiding the overflow prevents a faint scrollbar/caret artifact from
     appearing on the right edge in webkit. */
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  &::placeholder {
    color: var(--text-secondary, rgba(255, 255, 255, 0.5));
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }
`;

const SendButton = styled.button<{ $alignBottom?: boolean }>`
  flex-shrink: 0;
  align-self: ${({ $alignBottom }) =>
    $alignBottom === true ? 'flex-end' : 'center'};
  width: 28px;
  height: 28px;
  padding: 0;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 6px;
  background: ${paradigmLightBlue};
  color: var(--cta-text-color, #fff);
  cursor: pointer;

  &:disabled {
    background: var(--button-color, rgba(255, 255, 255, 0.08));
    color: var(--text-primary);
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;
